import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Clock, Info, List, Shield } from "lucide-react";
import * as React from "react";
import { Link, Outlet, useLocation } from "react-router";

import { EntityComments } from "@/shared/components/comments";
import { TagList } from "@/shared/components/TagList";
import { FormattedDateTime } from "@/shared/datetime";
import { CollectionRecord } from "@/shared/domain/collections";
import { Tab, Tabs, TabsList } from "@/shared/foundation/primitives";

import styles from "./CollectionDetails.module.css";
import { CollectionTypeChip } from "./CollectionTypeChip.tsx";
import { EditableCollectionDescription } from "./EditableCollectionDescription.tsx";
import { EditableCollectionName } from "./EditableCollectionName.tsx";
import {
  CollectionTab,
  useActiveCollectionTab,
} from "./useActiveCollectionTabFromUrl";

interface CollectionDetailsProps {
  collection: CollectionRecord;
}

const COLLECTION_TABS = [
  { tab: CollectionTab.Items, label: "Items", Icon: List },
  { tab: CollectionTab.Details, label: "Details", Icon: Info },
  { tab: CollectionTab.Activity, label: "Activity", Icon: Clock },
  { tab: CollectionTab.Permissions, label: "Permissions", Icon: Shield },
];

function totalItemCount(resources: CollectionRecord["resources"]): number {
  return Object.values(resources).reduce((sum, arr) => sum + arr.length, 0);
}

export const CollectionDetails: React.FC<CollectionDetailsProps> = ({
  collection,
}) => {
  const activeTab = useActiveCollectionTab();
  const location = useLocation();

  const itemCount = totalItemCount(collection.resources);

  return (
    <div className={styles.root}>
      {/* ── Header + Tab bar ── */}
      <div>
        <div className={styles.header}>
          {/* Left: name / description / metadata / tags */}
          <div className={styles.headerLeft}>
            <div className={styles.titleRow}>
              <FolderOutlinedIcon
                className={styles.folderIcon}
                fontSize="small"
              />
              <EditableCollectionName
                name={collection.name ?? ""}
                collectionId={collection.collection_id}
                orgId={collection.org_id}
              />
              <CollectionTypeChip resourceType={collection.resource_type} />
            </div>

            <EditableCollectionDescription
              description={collection.description ?? ""}
              collectionId={collection.collection_id}
              orgId={collection.org_id}
            />

            <div className={styles.metadataRow}>
              <PersonOutlinedIcon className={styles.metadataIcon} />
              <span className={styles.metadataLabel}>Created By:</span>
              <span className={styles.metadataText}>
                {collection.created_by}
              </span>
              <span className={styles.metadataDot}>·</span>
              <CalendarTodayOutlinedIcon className={styles.metadataIcon} />
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataText}>
                <FormattedDateTime value={collection.created} time="none" />
              </span>
              {itemCount > 0 && (
                <>
                  <span className={styles.metadataDot}>·</span>
                  <span className={styles.metadataText}>
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </span>
                </>
              )}
            </div>

            {collection.tags.length > 0 && (
              <div className={styles.tagsRow}>
                <TagList tags={collection.tags} />
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className={styles.tabBar}>
          <Tabs value={activeTab}>
            <TabsList aria-label="Collection">
              {COLLECTION_TABS.map(({ tab, label, Icon }) => (
                <Tab
                  key={tab}
                  value={tab}
                  nativeButton={false}
                  render={
                    <Link
                      to={{ pathname: tab, search: location.search }}
                      replace
                    />
                  }
                >
                  <Icon aria-hidden />
                  {label}
                </Tab>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Items tab needs flex-fill + internal scroll so the table fills the
          viewport. Other tabs use plain padding so <EntityComments> sits
          directly under the content rather than being pushed to the bottom. */}
      <div
        className={
          activeTab === CollectionTab.Items
            ? styles.itemsOutlet
            : styles.tabPanel
        }
      >
        <Outlet context={collection} />
      </div>

      <div className={styles.commentsSection}>
        <EntityComments
          entityType="collection"
          entityId={collection.collection_id}
        />
      </div>
    </div>
  );
};
